import brand from '../../config/brand';
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, ArrowLeft, Send, CheckCircle, AlertCircle, Loader2, Shield, KeyRound, Eye, EyeOff } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Flux client/partenaire (existant) ───────────────────────────────────────
const ClientForgotFlow = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [debugLink, setDebugLink] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setDebugLink('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
        if (data._debug?.reset_link) setDebugLink(data._debug.reset_link);
      } else {
        setError(data.detail || 'Une erreur est survenue');
      }
    } catch {
      setError('Impossible de contacter le serveur.');
    } finally {
      setLoading(false);
    }
  };

  if (success) return (
    <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-100">
      <div className="text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={32} className="text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Email envoyé !</h2>
        <p className="text-gray-600 mb-4 text-sm">
          Si cette adresse est associée à un compte, vous recevrez un lien de réinitialisation.
        </p>
        {debugLink && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-left">
            <p className="text-amber-800 text-xs font-semibold mb-1 flex items-center gap-1">
              <AlertCircle size={14} /> Mode Test — Email non envoyé
            </p>
            <a href={debugLink.replace('https://app.emergent.sh', '')}
               className="text-blue-600 text-xs break-all underline">
              {debugLink.replace('https://app.emergent.sh', window.location.origin)}
            </a>
          </div>
        )}
        <button onClick={() => navigate('/login')}
          className="w-full py-3 bg-gradient-to-r from-brand-primary to-brand-primary-hover text-white rounded-xl font-semibold transition-all">
          Retour à la connexion
        </button>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-100">
      <div className="text-center mb-6">
        <div className="w-14 h-14 bg-gradient-to-r from-brand-primary to-brand-primary-hover rounded-full flex items-center justify-center mx-auto mb-3">
          <Mail size={24} className="text-white" />
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-1">Mot de passe oublié ?</h1>
        <p className="text-gray-500 text-sm">Entrez votre email pour recevoir un lien de réinitialisation.</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <p className="text-red-600 text-sm bg-red-50 rounded-lg p-3">{error}</p>}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Adresse email</label>
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="votre@email.com" required
              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent text-sm" />
          </div>
        </div>
        <button type="submit" disabled={loading || !email}
          className="w-full py-3 bg-gradient-to-r from-brand-primary to-brand-primary-hover text-white rounded-xl font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2">
          {loading ? <><Loader2 size={18} className="animate-spin" /><span>Envoi...</span></> : <><Send size={16} /><span>Envoyer le lien</span></>}
        </button>
      </form>
    </div>
  );
};

// ─── Flux admin (3 étapes) ────────────────────────────────────────────────────
const AdminForgotFlow = ({ onToggleAdmin }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Étape 1
  const [email, setEmail] = useState('');
  const [backupEmail, setBackupEmail] = useState('');

  // Étape 2
  const [stepToken, setStepToken] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [showAnswer, setShowAnswer] = useState(false);

  // Étape 3
  const [debugLink, setDebugLink] = useState('');

  const handleStep1 = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/admin-forgot/step1`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, backup_email: backupEmail }),
      });
      const data = await res.json();
      if (res.ok) {
        setStepToken(data.step_token);
        setQuestion(data.security_question);
        setStep(2);
      } else {
        setError(data.detail || 'Paramètres invalides.');
      }
    } catch {
      setError('Impossible de contacter le serveur.');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/admin-forgot/step2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ step_token: stepToken, answer }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data._debug?.reset_link) setDebugLink(data._debug.reset_link);
        setStep(3);
      } else {
        setError(data.detail || 'Réponse incorrecte.');
      }
    } catch {
      setError('Impossible de contacter le serveur.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      {/* Header admin */}
      <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-4 flex items-center gap-3">
        <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center">
          <Shield size={18} className="text-white" />
        </div>
        <div>
          <p className="text-white font-semibold text-sm">Récupération Administrateur</p>
          <p className="text-slate-300 text-xs">Étape {step} / 3</p>
        </div>
        <div className="ml-auto flex gap-1">
          {[1,2,3].map(s => (
            <div key={s} className={`w-2 h-2 rounded-full transition-colors ${s <= step ? 'bg-white' : 'bg-white/30'}`} />
          ))}
        </div>
      </div>

      <div className="p-6">
        {error && (
          <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
            <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Étape 1 */}
        {step === 1 && (
          <form onSubmit={handleStep1} className="space-y-4" data-testid="admin-forgot-step1">
            <div className="text-center mb-4">
              <p className="font-semibold text-slate-800 text-sm">Vérification d'identité</p>
              <p className="text-slate-500 text-xs mt-1">Entrez votre email professionnel et votre email de secours</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Email professionnel</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="vous@asar-csg.com" required data-testid="admin-forgot-email"
                  className="w-full pl-8 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Email de secours (personnel)</label>
              <div className="relative">
                <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="email" value={backupEmail} onChange={e => setBackupEmail(e.target.value)}
                  placeholder="votre@gmail.com" required data-testid="admin-forgot-backup-email"
                  className="w-full pl-8 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent" />
              </div>
            </div>
            <button type="submit" disabled={loading || !email || !backupEmail}
              data-testid="admin-forgot-step1-submit"
              className="w-full py-2.5 bg-slate-700 hover:bg-slate-800 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowLeft size={16} className="rotate-180" />}
              Continuer
            </button>
          </form>
        )}

        {/* Étape 2 */}
        {step === 2 && (
          <form onSubmit={handleStep2} className="space-y-4" data-testid="admin-forgot-step2">
            <div className="text-center mb-4">
              <p className="font-semibold text-slate-800 text-sm">Question de sécurité</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <p className="text-xs text-slate-500 mb-1">Votre question :</p>
              <p className="text-sm font-medium text-slate-800" data-testid="admin-forgot-question">{question}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Votre réponse</label>
              <div className="relative">
                <input type={showAnswer ? 'text' : 'password'} value={answer} onChange={e => setAnswer(e.target.value)}
                  placeholder="Réponse (insensible à la casse)" required data-testid="admin-forgot-answer"
                  className="w-full pr-9 pl-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent" />
                <button type="button" onClick={() => setShowAnswer(p => !p)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1">
                  {showAnswer ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-1">La casse n'est pas prise en compte.</p>
            </div>
            <button type="submit" disabled={loading || !answer}
              data-testid="admin-forgot-step2-submit"
              className="w-full py-2.5 bg-slate-700 hover:bg-slate-800 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={14} />}
              Valider et envoyer le lien
            </button>
            <button type="button" onClick={() => { setStep(1); setError(''); }}
              className="w-full text-xs text-slate-400 hover:text-slate-600 py-1 transition-colors">
              Retour
            </button>
          </form>
        )}

        {/* Étape 3 — succès */}
        {step === 3 && (
          <div className="text-center" data-testid="admin-forgot-success">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle size={28} className="text-green-600" />
            </div>
            <p className="font-semibold text-slate-800 mb-2">Lien envoyé !</p>
            <p className="text-slate-500 text-sm mb-4">
              Un lien de réinitialisation a été envoyé sur votre email de secours. Il est valide 30 minutes.
            </p>
            {debugLink && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-left">
                <p className="text-amber-700 text-xs font-semibold mb-1">Mode Test — Email non envoyé</p>
                <a href={debugLink.replace('https://app.emergent.sh', '')}
                   className="text-blue-600 text-xs break-all underline">
                  {debugLink.replace('https://app.emergent.sh', window.location.origin)}
                </a>
              </div>
            )}
            <button onClick={() => navigate('/login')}
              className="w-full py-2.5 bg-slate-700 hover:bg-slate-800 text-white rounded-lg text-sm font-medium transition-colors">
              Retour à la connexion
            </button>
          </div>
        )}
      </div>

      {/* Lien retour vers login admin */}
      <div className="px-6 pb-4 text-center">
        <Link to="/admin/login" className="text-xs text-slate-400 hover:text-slate-600 transition-colors flex items-center justify-center gap-1">
          <ArrowLeft size={12} /> Retour à la connexion
        </Link>
      </div>
    </div>
  );
};

// ─── Page principale ──────────────────────────────────────────────────────────
const ForgotPassword = () => {
  const [searchParams] = useSearchParams();
  // admin=1 → activé depuis la page de connexion admin
  const [adminMode] = useState(searchParams.get('admin') === '1');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img src={brand.logo} alt={brand.name} className="h-16 sm:h-20 mx-auto" />
        </div>

        {adminMode
          ? <AdminForgotFlow onToggleAdmin={null} />
          : <ClientForgotFlow />
        }

        <div className="mt-5 text-center">
          <Link to="/login"
            className="inline-flex items-center gap-1 text-brand-primary hover:text-brand-primary-hover font-medium text-sm">
            <ArrowLeft size={14} /> Retour à la connexion
          </Link>
        </div>
        <p className="text-center text-gray-400 text-xs mt-4">© 2025 {brand.name}</p>
      </div>
    </div>
  );
};

export default ForgotPassword;
